import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MessageSquare, Send, Bot, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const ticketSchema = z.object({
  name: z.string().trim().min(1, "Nome é obrigatório").max(100),
  email: z.string().trim().email("E-mail inválido").max(255),
  message: z.string().trim().min(1, "Mensagem é obrigatória").max(2000),
});

type TicketForm = z.infer<typeof ticketSchema>;

export default function Suporte() {
  const [sending, setSending] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  

  const form = useForm<TicketForm>({
    resolver: zodResolver(ticketSchema),
    defaultValues: { name: "", email: "", message: "" },
  });

  useEffect(() => {
    async function prefill() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      if (user.email) {
        form.setValue("email", user.email);
        setUserEmail(user.email);
      }
      const { data } = await supabase.from("profiles").select("full_name").eq("id", user.id).single();
      if (data?.full_name) {
        form.setValue("name", data.full_name);
        setUserName(data.full_name);
      }
    }
    prefill();
  }, [form]);

  async function onSubmit(data: TicketForm) {
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: data,
      });
      if (error) throw error;
      toast.success("Chamado enviado com sucesso!");
      form.reset();
    } catch {
      toast.error("Erro ao enviar chamado. Tente novamente.");
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    if (!showChat) return;

    let pollInterval: ReturnType<typeof setInterval>;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://js.huggy.chat/widget.min.js";

    (window as any).$_Huggy = {
      defaultCountry: "+55",
      uuid: "6d4d2eca-9ae3-4a70-a4d1-c492692c7e4b",
      company: "376406",
    };
    (window as any).pwz = {
      context: { id: "a65e452a4e3c1544b84ff2157f553fba" },
    };

    script.onload = () => {
      pollInterval = setInterval(() => {
        if ((window as any).Huggy && typeof (window as any).Huggy.openBox === "function") {
          clearInterval(pollInterval);
          (window as any).Huggy.openBox();
          if (typeof (window as any).Huggy.setName === "function") {
            (window as any).Huggy.setName(userName || "");
          }
          if (typeof (window as any).Huggy.setEmail === "function") {
            (window as any).Huggy.setEmail(userEmail || "");
          }
          if (typeof (window as any).Huggy.sendMessage === "function") {
            (window as any).Huggy.sendMessage("Olá, preciso de suporte!");
          }
        }
      }, 300);
    };

    document.body.appendChild(script);

    return () => {
      clearInterval(pollInterval);
      try {
        if ((window as any).Huggy && typeof (window as any).Huggy.closeBox === "function") {
          (window as any).Huggy.closeBox();
        }
        document.body.removeChild(script);
      } catch { /* already removed */ }
      delete (window as any).$_Huggy;
      delete (window as any).pwz;
    };
  }, [showChat, userEmail, userName]);

  return (
    <div className="max-w-2xl mx-auto space-y-10 py-4">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Suporte</h1>
        <p className="text-muted-foreground">Como podemos ajudar?</p>
      </div>

      {/* Quick channels */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* WhatsApp */}
        <button
          onClick={() => window.open("https://wa.me/5554991488694", "_blank")}
          className="group flex items-center gap-4 rounded-2xl border border-border bg-gradient-to-br from-accent/60 to-background p-5 text-left transition-all hover:shadow-md hover:scale-[1.02]"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <MessageSquare className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground">WhatsApp</p>
            <p className="text-sm text-muted-foreground">Resposta rápida</p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </button>

        {/* Lia */}
        <div className="rounded-2xl border border-border bg-gradient-to-br from-accent/60 to-background p-5 transition-all hover:shadow-md">
          <button
            onClick={() => !showChat && setShowChat(true)}
            className="group flex w-full items-center gap-4 text-left"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Bot className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground">Lia – Agente Virtual</p>
              <p className="text-sm text-muted-foreground">Disponível 24h</p>
            </div>
            {!showChat && (
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            )}
          </button>

          {showChat && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => {
                try {
                  if ((window as any).Huggy?.closeBox) (window as any).Huggy.closeBox();
                  if ((window as any).Huggy?.hideChats) (window as any).Huggy.hideChats();
                  if ((window as any).Huggy?.destroy) (window as any).Huggy.destroy();
                } catch { /* ignore */ }
                setShowChat(false);
              }}
            >
              <X className="h-3.5 w-3.5 mr-1.5" />
              Encerrar atendimento
            </Button>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="relative flex items-center">
        <Separator className="flex-1" />
        <span className="mx-4 text-xs text-muted-foreground uppercase tracking-widest">ou</span>
        <Separator className="flex-1" />
      </div>

      {/* Form */}
      <div className="rounded-2xl bg-muted/30 p-6 sm:p-8">
        <h2 className="text-lg font-medium text-foreground mb-1">Envie uma mensagem</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Nossa equipe responderá pelo e-mail cadastrado.
        </p>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Seu nome" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="seu@email.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mensagem</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva como podemos ajudar..."
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={sending}>
                {sending ? "Enviando..." : "Enviar mensagem"}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </form>
        </Form>
      </div>

      {showChat && (
        <div ref={chatRef} id="huggy-chat-container" className="min-h-[100px]" />
      )}
    </div>
  );
}
